import { useCallback, useEffect, useRef, useState } from "react";
import { useLatest } from "hooks/useLatest";
import type {
  UseInfiniteCursorRequestArgs,
  UseInfiniteCursorRequestResult,
} from "./types";

export const useInfiniteCursorRequest = <TItem>({
  limit = 20,
  resetKey,
  service,
}: UseInfiniteCursorRequestArgs<TItem>): UseInfiniteCursorRequestResult<TItem> => {
  const serviceRef = useLatest(service);
  const resetKeyRef = useLatest(resetKey);
  const isFetchingRef = useRef(false);

  const [items, setItems] = useState<TItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const loadPage = useCallback(
    async (activeCursor: string | null, replace: boolean) => {
      // Snapshot the reset signal; if it changes mid-request (e.g. a view
      // switch) this response is stale and must not touch the new list.
      const requestedKey = resetKeyRef.current;
      isFetchingRef.current = true;
      setIsLoading(true);
      try {
        const page = await serviceRef.current({ cursor: activeCursor, limit });
        if (resetKeyRef.current !== requestedKey) return;
        setItems((previousItems) =>
          replace ? page.items : [...previousItems, ...page.items],
        );
        setCursor(page.nextCursor);
        setHasMore(page.hasMore);
        setError(null);
      } catch (caughtError) {
        if (resetKeyRef.current === requestedKey) setError(caughtError);
      } finally {
        if (resetKeyRef.current === requestedKey) {
          setIsLoading(false);
          isFetchingRef.current = false;
        }
      }
    },
    [limit, serviceRef, resetKeyRef],
  );

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMore) return;
    loadPage(cursor, false);
  }, [cursor, hasMore, loadPage]);

  // Initial load, and a fresh first page whenever resetKey changes.
  useEffect(() => {
    isFetchingRef.current = false;
    loadPage(null, true);
  }, [resetKey, loadPage]);

  return { items, setItems, isLoading, error, hasMore, loadMore };
};
